import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../contexts/AppContext';
import useSalesInvoiceService from '../../app/services/hooks/useSalesInvoiceService';
import { SalesInvoiceDataForm, SalesInvoiceFilters } from '../../app/models/SalesInvoice';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import useNavigationPage from '../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';

import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import SalesInvoiceDataTable from './components/tables/SalesInvoiceDataTable';
import SalesInvoiceCreate from './components/SalesInvoiceCreate';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Facturas de Venta',
        url: '/sales-invoices',
        isActive: true
    }
];

const SalesInvoices = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const { fetchingGetSalesInvoices, getSalesInvoices, deleteSalesInvoice } = useSalesInvoiceService();

    // Estados
    const [salesInvoices, setSalesInvoices] = useState<SalesInvoiceDataForm[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [showingCreate, setShowingCreate] = useState(false);

    // Filtros de la tabla
    const [filter, setFilter] = useState<SalesInvoiceFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    // Recargar tabla cuando cambian los filtros
    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        try {
            getSalesInvoices(filter, {
                onSuccess: (response: ServiceResponse) => {
                    setSalesInvoices(response.data?.sales_invoices || []);
                    setTotalRows(response.data?.total_rows || 0);
                },
                onError: (response: ServiceResponse) => {
                    console.error('Error al cargar facturas de venta:', response);
                    toast.error(response.message, { autoClose: 2000 });
                    setSalesInvoices([]);
                    setTotalRows(0);
                }
            });
        } catch (error) {
            console.error('Error en reloadTable:', error);
            setSalesInvoices([]);
            setTotalRows(0);
        }
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (invoiceId: number) => {
        navigationPage('/sales-invoices/' + invoiceId + '/edit');
    };

    const destroy = (invoiceId: number) => {
        const _text = 'Está a punto de eliminar la factura de venta #' + invoiceId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando factura...');

                        deleteSalesInvoice(invoiceId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La factura no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Facturas de Venta" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva Factura de Venta" />}
                />

                <SalesInvoiceDataTable
                    salesInvoices={salesInvoices}
                    totalRows={totalRows}
                    loading={fetchingGetSalesInvoices}
                    destroy={destroy}
                    edit={showEdit}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Factura de Venta"
                    backdrop={true}
                    showFooter={false}
                    size="lg"
                >
                    <SalesInvoiceCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default SalesInvoices;
